/**
 * «График»: как распланирована неделя подразделения в CRM. Спека
 * `docs/specs/2026-09-24-schedule-design.md`; числа — те же, что у
 * `prokat-runner schedule-week`, из одного модуля на сервере. Вкладка
 * «План-факт» (`?tab=fact`) — график против оплачиваемых статусов и акцепта.
 * Вкладка «По неделям» (`?tab=weeks&year=&group=4`) — акцепт сотрудников
 * неделя за неделей, спека `docs/specs/2026-10-08-schedule-weeks-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useSearchParams } from "react-router";

import {
  hours,
  loadSchedule,
  loadScheduleWeeks,
  type Fact,
  type Person,
  type ScheduleWeek,
  type ScheduleWeeks,
  type WeeksPeriod,
} from "@/lib/schedule";

const KC = 3;
const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

const today = () => new Date().toLocaleDateString("sv-SE");

/** Сдвиг даты ГГГГ-ММ-ДД на `days` суток; полдень — чтобы переход на летнее время не съел день. */
function shift(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("sv-SE");
}

const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;

/** Факт к плану: ≥ 90 % — норма, ≥ 75 % — жёлтый, ниже — красный. */
function tone(fact: number, plan: number): string {
  if (!plan) return "";
  const pct = fact / plan;
  return pct >= 0.9 ? "text-ok" : pct >= 0.75 ? "text-warn" : "text-err";
}

const pct = (fact: number, plan: number) => (plan ? `${Math.round((fact / plan) * 100)} %` : "—");

/** Отклонение факта от плана: «−1,5 ч» / «+0,5 ч». */
function diff(fact: number, plan: number): string {
  const d = fact - plan;
  return `${d > 0 ? "+" : d < 0 ? "−" : ""}${hours(Math.abs(d))}`;
}

/** Факт дня сотрудника под интервалами графика. */
function FactLine({ plan, fact, today }: { plan: number; fact: Fact | undefined; today: boolean }) {
  if (!fact?.minutes)
    return plan && !today ? <div className="text-[11.5px] text-err">не вышел</div> : null;
  return (
    <div className={`text-[11.5px] ${plan ? tone(fact.minutes, plan) : "text-warn"}`} title="часы в оплачиваемых статусах">
      {plan ? "факт" : "без графика"} {hours(fact.minutes)}
      {today ? <span className="text-fg-faint"> · идёт</span> : null}
    </div>
  );
}

function Days({ r }: { r: ScheduleWeek }) {
  const max = Math.max(...r.days.map((d) => d.staff), 1);
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4" data-annot="график по дням">
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-3 py-1.5 font-medium" />
              {r.days.map((d, i) => (
                <th key={d.date} className={`whitespace-nowrap px-3 py-1.5 text-right font-medium ${i >= 5 ? "text-fg-muted" : ""}`}>
                  {WEEKDAYS[i]} {dm(d.date)}
                </th>
              ))}
              <th className="whitespace-nowrap px-3 py-1.5 text-right font-medium">Неделя</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-border" data-annot="часов по графику">
              <td className="whitespace-nowrap px-3 py-2 text-fg-muted">Часов</td>
              {r.days.map((d) => (
                <td key={d.date} className="px-3 py-2 text-right text-[18px] font-semibold tabular-nums">
                  {d.minutes ? hours(d.minutes) : <span className="text-fg-faint">—</span>}
                </td>
              ))}
              <td className="px-3 py-2 text-right text-[18px] font-semibold tabular-nums">{hours(r.total.minutes)}</td>
            </tr>
            <tr className="border-t border-border" data-annot="сотрудников в смене">
              <td className="whitespace-nowrap px-3 py-2 text-fg-muted">Сотрудников</td>
              {r.days.map((d) => (
                <td key={d.date} className="px-3 py-2 text-right">
                  <div className={`text-[18px] font-semibold tabular-nums ${d.staff ? "" : "text-err"}`}>{d.staff}</div>
                  <div className="ml-auto mt-1 h-1 rounded bg-fg-faint/20">
                    <div className="h-1 rounded bg-fg-muted" style={{ width: `${(d.staff / max) * 100}%` }} />
                  </div>
                </td>
              ))}
              <td className="px-3 py-2 text-right tabular-nums">
                <div className="text-[18px] font-semibold">{r.total.staff}</div>
                <div className="text-[11.5px] text-fg-faint">смен {r.total.shifts}</div>
              </td>
            </tr>
            <tr className="border-t border-border" data-annot="факт по статусам">
              <td className="whitespace-nowrap px-3 py-2 text-fg-muted" title="часы в оплачиваемых статусах CRM">
                Факт
              </td>
              {r.days.map((d) => (
                <td key={d.date} className="px-3 py-2 text-right tabular-nums">
                  {d.fact === null ? (
                    <span className="text-fg-faint">—</span>
                  ) : (
                    <>
                      <div className={`text-[15px] font-semibold ${d.date < r.today ? tone(d.fact, d.minutes) : ""}`}>
                        {hours(d.fact)}
                      </div>
                      <div className="text-[11.5px] text-fg-faint">
                        {d.date === r.today ? "идёт" : `${pct(d.fact, d.minutes)} · чел. ${d.fact_staff}`}
                      </div>
                    </>
                  )}
                </td>
              ))}
              <td className="px-3 py-2 text-right tabular-nums">
                <div className={`text-[15px] font-semibold ${tone(r.total.fact, r.total.plan_to_date)}`}>{hours(r.total.fact)}</div>
                <div className="text-[11.5px] text-fg-faint" title="к плану прошедших дней">
                  {pct(r.total.fact, r.total.plan_to_date)} из {hours(r.total.plan_to_date)}
                </div>
              </td>
            </tr>
            <tr className="border-t border-border" data-annot="акцепт">
              <td className="whitespace-nowrap px-3 py-2 text-fg-muted" title="подтверждённое время, sUser_time_confirm">
                Акцепт
              </td>
              {r.days.map((d) => (
                <td key={d.date} className="px-3 py-2 text-right tabular-nums">
                  {d.accept === null || d.date === r.today ? (
                    <span className="text-fg-faint">—</span>
                  ) : d.accept ? (
                    hours(d.accept)
                  ) : (
                    <span className="text-[11.5px] text-fg-faint">не подтв.</span>
                  )}
                </td>
              ))}
              <td className="px-3 py-2 text-right tabular-nums">{r.total.accept ? hours(r.total.accept) : "—"}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function People({ r }: { r: ScheduleWeek }) {
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="график по сотрудникам">
      <h2 className="border-b border-border px-4 py-3 text-[13px] font-medium">Сотрудники: {r.people.length}</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-2 font-medium">Сотрудник</th>
              {r.days.map((d, i) => (
                <th key={d.date} className="whitespace-nowrap px-3 py-2 font-medium">
                  {WEEKDAYS[i]} {dm(d.date)}
                </th>
              ))}
              <th className="px-4 py-2 text-right font-medium">Итого</th>
            </tr>
          </thead>
          <tbody>
            {r.people.map((p) => (
              <tr key={p.user_id} className="border-t border-border hover:bg-bg-hover" data-annot={`сотрудник ${p.name}`}>
                <td className="whitespace-nowrap px-4 py-1.5">{p.name}</td>
                {r.days.map((d) => {
                  const c = p.days[d.date];
                  const f = p.fact[d.date];
                  return (
                    <td key={d.date} className="whitespace-nowrap px-3 py-1.5 align-top tabular-nums">
                      {c ? (
                        <>
                          <span title={c.extra ? "доп. смена" : undefined} className={c.extra ? "text-warn" : ""}>
                            {c.intervals.map((i) => `${i.from}–${i.to}`).join(", ")}
                          </span>
                          <span className="ml-1.5 text-[11.5px] text-fg-faint">{hours(c.minutes)}</span>
                        </>
                      ) : f?.minutes ? null : (
                        <span className="text-fg-faint">—</span>
                      )}
                      {d.date <= r.today ? <FactLine plan={c?.minutes ?? 0} fact={f} today={d.date === r.today} /> : null}
                    </td>
                  );
                })}
                <td className="whitespace-nowrap px-4 py-1.5 text-right align-top tabular-nums">
                  {hours(p.minutes)} <span className="text-fg-faint">· {p.shifts} см.</span>
                  {p.plan_to_date || p.fact_minutes ? (
                    <div className={`text-[11.5px] ${tone(p.fact_minutes, p.plan_to_date)}`} title="факт к плану прошедших дней">
                      факт {hours(p.fact_minutes)} из {hours(p.plan_to_date)}
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11.5px] text-fg-faint">
        Время местное для подразделения. Жёлтым — доп. смена. Факт — часы в оплачиваемых статусах CRM;
        цвет — к плану дня: ≥ 90 % зелёный, ≥ 75 % жёлтый, ниже красный.
      </div>
    </section>
  );
}

function Stat({ label, value, sub, className = "" }: { label: string; value: string; sub?: string; className?: string }) {
  return (
    <div className="min-w-[140px] rounded-[var(--radius-card)] border border-border bg-bg-subtle px-4 py-3">
      <div className="text-[11.5px] text-fg-faint">{label}</div>
      <div className={`mt-1 text-[22px] font-semibold tabular-nums tracking-[-0.02em] ${className}`}>{value}</div>
      {sub ? <div className="text-[11.5px] text-fg-faint">{sub}</div> : null}
    </div>
  );
}

function FactCell({ p, day, today }: { p: Person; day: string; today: string }) {
  const plan = p.days[day]?.minutes ?? 0;
  const f = p.fact[day];
  if (day > today) return plan ? <span className="text-fg-faint">{hours(plan)}</span> : <span className="text-fg-faint">—</span>;
  if (!plan && !f?.minutes) return <span className="text-fg-faint">—</span>;
  return (
    <div className="leading-tight">
      <div className="text-[11.5px] text-fg-faint">{plan ? hours(plan) : "без графика"}</div>
      {f?.minutes ? (
        <div className={`font-medium ${day === today ? "" : plan ? tone(f.minutes, plan) : "text-warn"}`}>
          {hours(f.minutes)}
          {day === today ? <span className="text-[11.5px] font-normal text-fg-faint"> · идёт</span> : null}
        </div>
      ) : day === today ? (
        <div className="text-[11.5px] text-fg-faint">ещё нет</div>
      ) : (
        <div className="font-medium text-err">не вышел</div>
      )}
    </div>
  );
}

/** Итог дня по всем: план, факт и отклонение — переработка плюсом и жёлтым. */
function DayTotalCell({ d, today }: { d: ScheduleWeek["days"][number]; today: string }) {
  if (d.date > today || d.fact === null)
    return <span className="text-fg-faint">{d.minutes ? hours(d.minutes) : "—"}</span>;
  if (!d.minutes && !d.fact) return <span className="text-fg-faint">—</span>;
  const over = d.fact > d.minutes;
  return (
    <div className="leading-tight">
      <div className="text-[11.5px] text-fg-muted">план {hours(d.minutes)}</div>
      <div className={`font-semibold ${d.date === today ? "" : over ? "text-warn" : tone(d.fact, d.minutes)}`}>
        факт {hours(d.fact)}
      </div>
      <div className={`text-[11.5px] ${d.date === today ? "text-fg-faint" : over ? "font-medium text-warn" : "text-fg-muted"}`}>
        {d.date === today ? "идёт" : `${diff(d.fact, d.minutes)}${over ? " переработка" : ""}`}
      </div>
    </div>
  );
}

function PlanFact({ r }: { r: ScheduleWeek }) {
  const t = r.total;
  const people = r.people.filter((p) => p.shifts || p.fact_minutes || Object.keys(p.fact).length);
  const absent = people.reduce((s, p) => s + p.absent, 0);
  const unplanned = people.reduce((s, p) => s + p.unplanned, 0);
  if (!t.plan_to_date && !t.fact)
    return (
      <div className="text-[12.5px] text-fg-muted">
        {r.week.start >= r.today ? "Неделя ещё не прошла — факта нет." : "Ни графика, ни факта за прошедшие дни."}
      </div>
    );
  return (
    <>
      <div className="flex flex-wrap gap-3" data-annot="план-факт итог">
        <Stat label="План (прошедшие дни)" value={hours(t.plan_to_date)} sub={`смен в графике: ${people.reduce((s, p) => s + Object.keys(p.days).filter((k) => k < r.today).length, 0)}`} />
        <Stat
          label="Факт по статусам"
          value={hours(t.fact)}
          sub={`${pct(t.fact, t.plan_to_date)} плана · ${diff(t.fact, t.plan_to_date)}`}
          className={tone(t.fact, t.plan_to_date)}
        />
        <Stat label="Акцепт" value={t.accept ? hours(t.accept) : "—"} sub={t.accept ? `${pct(t.accept, t.plan_to_date)} плана` : "ещё не подтверждён"} />
        <Stat label="Не вышли" value={String(absent)} sub="смен в графике без статусов" className={absent ? "text-err" : ""} />
        <Stat label="Без графика" value={String(unplanned)} sub="дней работы вне графика" className={unplanned ? "text-warn" : ""} />
      </div>
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="план-факт по сотрудникам">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-2 font-medium">Сотрудник</th>
                {r.days.map((d, i) => (
                  <th key={d.date} className={`whitespace-nowrap px-3 py-2 font-medium ${d.date === r.today ? "text-fg" : ""}`}>
                    {WEEKDAYS[i]} {dm(d.date)}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-medium">План</th>
                <th className="px-3 py-2 text-right font-medium">Факт</th>
                <th className="px-3 py-2 text-right font-medium">Откл.</th>
                <th className="px-3 py-2 text-right font-medium">%</th>
                <th className="px-3 py-2 text-right font-medium">Акцепт</th>
                <th className="px-4 py-2 text-right font-medium" title="не вышел / без графика">Смены</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border bg-bg-hover/60" data-annot="план-факт итог по дням">
                <td className="whitespace-nowrap px-4 py-1.5 font-medium">Итого за день</td>
                {r.days.map((d) => (
                  <td key={d.date} className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                    <DayTotalCell d={d} today={r.today} />
                  </td>
                ))}
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium tabular-nums">{hours(t.plan_to_date)}</td>
                <td className={`whitespace-nowrap px-3 py-1.5 text-right font-semibold tabular-nums ${t.fact > t.plan_to_date ? "text-warn" : tone(t.fact, t.plan_to_date)}`}>
                  {hours(t.fact)}
                </td>
                <td className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${t.fact > t.plan_to_date ? "font-medium text-warn" : "text-fg-muted"}`}>
                  {diff(t.fact, t.plan_to_date)}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{pct(t.fact, t.plan_to_date)}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{t.accept ? hours(t.accept) : "—"}</td>
                <td className="whitespace-nowrap px-4 py-1.5 text-right tabular-nums text-fg-faint">—</td>
              </tr>
              {people.map((p) => (
                <tr key={p.user_id} className="border-t border-border hover:bg-bg-hover" data-annot={`план-факт ${p.name}`}>
                  <td className="whitespace-nowrap px-4 py-1.5">{p.name || `#${p.user_id}`}</td>
                  {r.days.map((d) => (
                    <td key={d.date} className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                      <FactCell p={p} day={d.date} today={r.today} />
                    </td>
                  ))}
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{hours(p.plan_to_date)}</td>
                  <td className={`whitespace-nowrap px-3 py-1.5 text-right font-medium tabular-nums ${tone(p.fact_minutes, p.plan_to_date)}`}>
                    {hours(p.fact_minutes)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-fg-muted">{diff(p.fact_minutes, p.plan_to_date)}</td>
                  <td className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${tone(p.fact_minutes, p.plan_to_date)}`}>
                    {pct(p.fact_minutes, p.plan_to_date)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{p.accept_minutes ? hours(p.accept_minutes) : "—"}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 text-right tabular-nums">
                    {p.absent ? <span className="text-err">не вышел {p.absent}</span> : null}
                    {p.absent && p.unplanned ? " · " : null}
                    {p.unplanned ? <span className="text-warn">вне графика {p.unplanned}</span> : null}
                    {!p.absent && !p.unplanned ? <span className="text-fg-faint">—</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-[11.5px] text-fg-faint">
          Первая строка — итог дня по всем: план, факт и отклонение, переработка — жёлтым «+». В ячейке — план дня (сверху) и факт: часы в оплачиваемых статусах CRM («Работаю», «Задача»), как на
          «Активности». Итоги — по прошедшим дням, сегодня ещё идёт. Акцепт подтверждают через 1–6 дней.
        </div>
      </section>
    </>
  );
}

/** Часы в плотной таблице: без «ч», до десятой. */
const h = (minutes: number) => (minutes / 60).toLocaleString("ru-RU", { maximumFractionDigits: 1 });

/** Период к предыдущему (более старому): «−8 %». */
function Delta({ now, before }: { now: number; before: number | undefined }) {
  if (!before) return null;
  const d = Math.round((now / before - 1) * 100);
  if (!d) return <span className="text-fg-faint">0 %</span>;
  return <span className={d > 0 ? "text-ok" : "text-err"}>{`${d > 0 ? "+" : "−"}${Math.abs(d)} %`}</span>;
}

function PeriodHead({ p, group }: { p: WeeksPeriod; group: number }) {
  return (
    <th className={`whitespace-nowrap px-2.5 py-2 text-right font-medium ${p.weeks < group ? "text-fg-faint" : ""}`}>
      <div>{p.weeks === 1 ? `нед. ${p.week_to}` : `нед. ${p.week_from}–${p.week_to}`}</div>
      <div className="font-normal">
        {dm(p.start)}–{dm(p.end)}
      </div>
      {p.weeks < group ? <div className="font-normal">неполный: {p.weeks} нед.</div> : null}
    </th>
  );
}

/** Акцепт сотрудников по неделям года: слева — прошлая неделя, дальше — в прошлое. */
function Weeks({ r }: { r: ScheduleWeeks }) {
  if (!r.periods.length)
    return <div className="text-[12.5px] text-fg-muted">В {r.year} году ещё нет ни одной закрытой недели.</div>;
  if (!r.people.length)
    return (
      <div className="text-[12.5px] text-fg-muted">
        За {r.year} год у «{r.location.name}» акцепта нет.
      </div>
    );
  const first = "sticky left-0 z-10 whitespace-nowrap bg-bg-subtle px-4 py-1.5 text-left";
  // Сравниваем только полные периоды: неполный — не спад, а короткий отрезок.
  const before = (i: number) => {
    const next = r.periods[i + 1];
    return next && next.weeks === r.periods[i]!.weeks ? next : undefined;
  };
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="акцепт по неделям">
      <h2 className="border-b border-border px-4 py-3 text-[13px] font-medium">
        Акцепт, часы · {r.year} · сотрудников: {r.total.staff}
      </h2>
      <div className="overflow-x-auto">
        <table className="text-[12.5px] tabular-nums">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className={`${first} align-bottom font-medium`}>Сотрудник</th>
              <th className="whitespace-nowrap px-2.5 py-2 text-right align-bottom font-medium">Год</th>
              {r.periods.map((p) => (
                <PeriodHead key={p.key} p={p} group={r.group} />
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-border" data-annot="акцепт всех по неделям">
              <td className={`${first} bg-bg-hover align-top font-medium`}>Все сотрудники</td>
              <td className="whitespace-nowrap bg-bg-hover/60 px-2.5 py-1.5 text-right align-top font-semibold">{h(r.total.minutes)}</td>
              {r.periods.map((p, i) => (
                <td key={p.key} className="whitespace-nowrap bg-bg-hover/60 px-2.5 py-1.5 text-right align-top">
                  <div className="font-semibold">{p.minutes ? h(p.minutes) : <span className="text-fg-faint">—</span>}</div>
                  <div className="text-[11.5px] text-fg-faint">
                    чел. {p.staff}
                    {before(i) ? " · " : null}
                    <Delta now={p.minutes} before={before(i)?.minutes} />
                  </div>
                </td>
              ))}
            </tr>
            {r.people.map((p) => {
              const max = Math.max(...Object.values(p.periods), 0);
              return (
                <tr key={p.user_id} className="border-t border-border hover:bg-bg-hover" data-annot={`акцепт ${p.name}`}>
                  <td className={`${first} ${p.active ? "" : "text-fg-faint"}`}>
                    {p.name || `#${p.user_id}`}
                    {!p.active && <span className="ml-1.5 text-[11.5px]">уволен</span>}
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-1.5 text-right font-medium">{h(p.minutes)}</td>
                  {r.periods.map((period) => {
                    const v = p.periods[period.key] ?? 0;
                    // Фон — доля от лучшего периода самого сотрудника: спад виден по строке.
                    const heat = v > 0 && max > 0 ? Math.round((v / max) * 26) : 0;
                    return (
                      <td
                        key={period.key}
                        className="whitespace-nowrap px-2.5 py-1.5 text-right"
                        style={heat ? { background: `color-mix(in srgb, var(--accent) ${heat}%, transparent)` } : undefined}
                      >
                        {v ? h(v) : <span className="text-fg-faint">·</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11.5px] text-fg-faint">
        Часы акцепта — подтверждённое руководителем время (по нему считают оплату), неделя Пн–Вс по местному времени
        подразделения. Слева — прошлая неделя, дальше — в прошлое; текущая не показана: она ещё идёт. Акцепт проставляют с
        опозданием в 1–6 дней, поэтому прошлая неделя может ещё вырасти. Под итогом — сколько человек работало и изменение
        к предыдущему периоду. Фон ячейки — доля от лучшего периода самого сотрудника. В списке и те, кто уже уволен.
      </div>
    </section>
  );
}

const TABS = [
  { id: "plan", label: "График" },
  { id: "fact", label: "План-факт" },
  { id: "weeks", label: "По неделям" },
] as const;

const GROUPS = [
  { id: 1, label: "Неделя" },
  { id: 4, label: "4 недели" },
] as const;

export function SchedulePage() {
  const [params, setParams] = useSearchParams();
  const location = Number(params.get("location")) || KC;
  const day = params.get("date") || today();
  const tab = TABS.find((t) => t.id === params.get("tab"))?.id ?? "plan";
  const year = Number(params.get("year")) || Number(today().slice(0, 4));
  const group = params.get("group") === "4" ? 4 : 1;
  const set = (next: { location?: number; date?: string; tab?: string; year?: number; group?: number }) => {
    const out: Record<string, string> = { location: String(next.location ?? location) };
    const t = next.tab ?? tab;
    if (t !== "plan") out.tab = t;
    if (t === "weeks") {
      out.year = String(next.year ?? year);
      if ((next.group ?? group) === 4) out.group = "4";
    } else out.date = next.date ?? day;
    setParams(out, { replace: true });
  };
  const week = useQuery({
    queryKey: ["schedule-week", location, day],
    queryFn: () => loadSchedule(location, day),
    enabled: tab !== "weeks",
  });
  const weeks = useQuery({
    queryKey: ["schedule-weeks", location, year, group],
    queryFn: () => loadScheduleWeeks(location, year, group),
    enabled: tab === "weeks",
  });
  const r = week.data;
  const locations = (tab === "weeks" ? weeks.data : r)?.locations;

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3" data-annot="шапка графика">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">График</h1>
        <div role="tablist" aria-label="Разделы графика" className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => set({ tab: t.id })}
              className={`h-8 rounded-[var(--radius-ui)] px-3 text-[12.5px] ${
                tab === t.id ? "bg-bg-hover font-medium text-fg" : "text-fg-muted hover:text-fg"
              }`}
              data-annot={`вкладка «${t.label}»`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <select
          value={location}
          onChange={(e) => set({ location: Number(e.target.value) })}
          className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg"
          data-annot="выбор подразделения"
        >
          {(locations ?? [{ id: KC, name: "Контакт Центр", short: "КЦ", tz: 0 }]).map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
        {tab === "weeks" ? (
          <div className="ml-auto flex items-center gap-3 text-[12.5px]">
            <div role="tablist" aria-label="Шаг периода" className="flex gap-1" data-annot="шаг: неделя или 4 недели">
              {GROUPS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  role="tab"
                  aria-selected={group === g.id}
                  onClick={() => set({ group: g.id })}
                  className={`h-8 rounded-[var(--radius-ui)] px-3 ${
                    group === g.id ? "bg-bg-hover font-medium text-fg" : "text-fg-muted hover:text-fg"
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <select
              value={year}
              onChange={(e) => set({ year: Number(e.target.value) })}
              className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg"
              data-annot="выбор года"
            >
              {(weeks.data?.years ?? [year]).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="ml-auto flex items-center gap-1 text-[12.5px]" data-annot="выбор недели">
            <button
              type="button"
              onClick={() => set({ date: shift(day, -7) })}
              className="grid h-8 w-8 place-items-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover"
              title="Предыдущая неделя"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="min-w-[150px] text-center tabular-nums text-fg-muted">
              {r ? `${dm(r.week.start)} — ${dm(r.week.end)} · нед. ${r.week.iso.slice(6)}` : "…"}
            </span>
            <button
              type="button"
              onClick={() => set({ date: shift(day, 7) })}
              className="grid h-8 w-8 place-items-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover"
              title="Следующая неделя"
            >
              <ChevronRight size={14} />
            </button>
            <button
              type="button"
              onClick={() => set({ date: today() })}
              className="ml-1 h-8 rounded-[var(--radius-ui)] border border-border px-2.5 text-fg-muted hover:bg-bg-hover"
            >
              текущая
            </button>
          </div>
        )}
      </div>
      {tab === "weeks" ? (
        weeks.isPending ? (
          <div className="text-[12.5px] text-fg-muted">Читаю акцепт из CRM…</div>
        ) : weeks.isError ? (
          <div className="text-[12.5px] text-err">Не удалось получить акцепт: {String(weeks.error.message)}</div>
        ) : (
          <Weeks r={weeks.data} />
        )
      ) : week.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Читаю график из CRM…</div>
      ) : week.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить график: {String(week.error.message)}</div>
      ) : tab === "fact" ? (
        <PlanFact r={r!} />
      ) : !r!.filled ? (
        <section className="rounded-[var(--radius-card)] border border-err/40 bg-bg-subtle p-4 text-[13px]" data-annot="график не заполнен">
          <div className="font-medium text-err">График на эту неделю не заполнен</div>
          <div className="mt-1 text-fg-muted">
            {r!.last_planned
              ? `Последний день в графике «${r!.location.name}» — ${dm(r!.last_planned)}.${r!.last_planned.slice(0, 4)}.`
              : `В CRM графика «${r!.location.name}» нет ни на один день.`}
          </div>
        </section>
      ) : (
        <>
          <Days r={r!} />
          <People r={r!} />
        </>
      )}
    </main>
  );
}
